import { createContext, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtDate, todayISO, can } from "../lib/api";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Users, Stethoscope, Dumbbell,
  Cpu, CreditCard, Receipt, Wallet, FileText, BarChart3, Settings, Search,
  LogOut, Plus, Menu, BriefcaseMedical, Bell, Archive, Megaphone, History,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import AppointmentModal from "./modals/AppointmentModal";
import PatientModal from "./modals/PatientModal";
import PaymentModal from "./modals/PaymentModal";
import ExpenseModal from "./modals/ExpenseModal";
import GymModal from "./modals/GymModal";
import { toast } from "sonner";
import { APP_VERSION, CHANGELOG, UPDATE_NOTICE_ROLES, UPDATE_NOTICE_EXTRA_NAMES } from "../data/changelog";

const QuickCtx = createContext(null);
export const useQuick = () => useContext(QuickCtx);

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/agenda", label: "Agenda di oggi", icon: ClipboardList, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/palestra", label: "Check-in", icon: Dumbbell, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/pazienti", label: "Pazienti", icon: Users, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/medici", label: "Medici", icon: BriefcaseMedical, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/fisioterapisti", label: "Fisioterapisti", icon: Stethoscope, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/macchinari", label: "Macchinari", icon: Cpu, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/pagamenti", label: "Pagamenti", icon: CreditCard, roles: ["admin", "segreteria", "coordinatore"] },
  { to: "/spese", label: "Spese", icon: Receipt, roles: ["admin", "segreteria", "coordinatore"] },
  { to: "/cassa", label: "Cassa", icon: Wallet, roles: ["admin", "segreteria", "coordinatore"] },
  { to: "/documenti", label: "Documenti", icon: FileText, roles: ["admin", "segreteria", "coordinatore", "fisioterapista"] },
  { to: "/report", label: "Report", icon: BarChart3, roles: ["admin", "segreteria", "coordinatore"] },
  { to: "/backup", label: "Backup", icon: Archive, roles: ["admin", "segreteria", "coordinatore"], strictRoles: true },
  { to: "/aggiornamenti", label: "Aggiornamenti", icon: History, roles: ["admin", "titolare", "segreteria", "coordinatore"], alwaysVisible: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
];

const SECTION_BY_PATH = {
  "/": "dashboard", "/calendario": "calendario", "/agenda": "agenda", "/palestra": "checkin",
  "/pazienti": "pazienti", "/medici": "medici", "/fisioterapisti": "fisioterapisti",
  "/macchinari": "macchinari", "/pagamenti": "pagamenti", "/spese": "spese", "/cassa": "cassa",
  "/documenti": "documenti", "/report": "report", "/backup": "backup", "/impostazioni": "impostazioni",
};

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.length < 2) { setRes(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setRes(data);
        setOpen(true);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="relative w-full max-w-md">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        data-testid="global-search-input"
        className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-100/80 border border-transparent focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm transition-all"
        placeholder="Cerca paziente, appuntamento, fisioterapista..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && res && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-slate-100 rounded-2xl shadow-xl max-h-96 overflow-y-auto p-2" data-testid="global-search-results">
          {res.patients.length > 0 && <p className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pt-1">Pazienti</p>}
          {res.patients.map((p) => (
            <button key={p.id} data-testid={`search-patient-${p.id}`} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-50 flex justify-between"
              onClick={() => { navigate(`/pazienti/${p.id}`); setQ(""); setOpen(false); }}>
              <span className="font-medium text-sm">{p.first_name} {p.last_name}</span>
              <span className="text-xs text-slate-400">{p.phone}</span>
            </button>
          ))}
          {res.appointments.length > 0 && <p className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pt-2">Appuntamenti</p>}
          {res.appointments.slice(0, 5).map((a) => (
            <button key={a.id} data-testid={`search-appointment-${a.id}`} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-50"
              onClick={() => { navigate("/calendario"); setQ(""); setOpen(false); }}>
              <span className="text-sm">{fmtDate(a.date)} {a.start} — {a.patient_name}</span>
            </button>
          ))}
          {res.physios.filter((p) => (p.category || "fisioterapista") === "medico").length > 0 && (
            <p className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pt-2">Medici</p>
          )}
          {res.physios.filter((p) => (p.category || "fisioterapista") === "medico").map((p) => (
            <button key={p.id} data-testid={`search-doctor-${p.id}`} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-50"
              onClick={() => { navigate(`/medici?focus=${p.id}`); setQ(""); setOpen(false); }}>
              <span className="text-sm">{p.first_name} {p.last_name}</span>
            </button>
          ))}
          {res.physios.filter((p) => (p.category || "fisioterapista") !== "medico").length > 0 && (
            <p className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pt-2">Fisioterapisti</p>
          )}
          {res.physios.filter((p) => (p.category || "fisioterapista") !== "medico").map((p) => (
            <button key={p.id} data-testid={`search-physio-${p.id}`} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-50"
              onClick={() => { navigate(`/fisioterapisti?focus=${p.id}`); setQ(""); setOpen(false); }}>
              <span className="text-sm">{p.first_name} {p.last_name}</span>
            </button>
          ))}
          {res.patients.length === 0 && res.appointments.length === 0 && res.physios.length === 0 && (
            <p className="text-sm text-slate-400 px-3 py-4" data-testid="search-empty">Nessun risultato</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quickOpen, setQuickOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null); // {type, prefill}

  const openModal = (type, prefill = null) => { setQuickOpen(false); setModal({ type, prefill }); };
  const closeModal = () => setModal(null);

  const permsReady = Boolean(user?.effective_permissions && Object.keys(user.effective_permissions).length);
  const nav = NAV.filter((n) => {
    if (n.strictRoles && !n.roles.includes(user?.role)) return false;
    if (n.alwaysVisible) return n.roles.includes(user?.role);
    return permsReady ? can(user, SECTION_BY_PATH[n.to] || "dashboard") : n.roles.includes(user?.role);
  });
  const navLabel = (n) => (n.to === "/" && user?.role === "fisioterapista" ? "La mia giornata" : n.label);

  const [inbox, setInbox] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [alertComposerOpen, setAlertComposerOpen] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertTarget, setAlertTarget] = useState("");
  const [alertRecipients, setAlertRecipients] = useState({ groups: [], users: [] });
  const [secretaryAlerts, setSecretaryAlerts] = useState([]);
  const activeSecretaryAlert = secretaryAlerts[0] || null;
  const [updateNoticeOpen, setUpdateNoticeOpen] = useState(false);
  const latestUpdate = CHANGELOG[0] || null;
  const canSendInternalMessage = ["admin", "titolare", "segreteria", "coordinatore"].includes(user?.role);
  const updateNoticeEligible = Boolean(
    UPDATE_NOTICE_ROLES.includes(user?.role) ||
    UPDATE_NOTICE_EXTRA_NAMES.some((name) => (user?.name || "").trim().toLowerCase().includes(name))
  );

  useEffect(() => {
    if (!user || !latestUpdate || !updateNoticeEligible) {
      setUpdateNoticeOpen(false);
      return;
    }
    setUpdateNoticeOpen(user.last_seen_update_version !== APP_VERSION);
  }, [user?.id, user?.last_seen_update_version, updateNoticeEligible, latestUpdate]);
  useEffect(() => {
    const f = async () => {
      try { const { data } = await api.get("/notes/inbox"); setInbox(data); } catch { /* noop */ }
    };
    f();
    const t = setInterval(f, 60000);
    const h = () => f();
    window.addEventListener("ll:data-changed", h);
    return () => { clearInterval(t); window.removeEventListener("ll:data-changed", h); };
  }, []);

  useEffect(() => {
    let active = true;
    const fetchAlerts = async () => {
      try {
        const { data } = await api.get("/operational-alerts/unread");
        if (active) setSecretaryAlerts(data.alerts || []);
      } catch { /* noop */ }
    };
    fetchAlerts();
    const timer = setInterval(fetchAlerts, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [user?.id]);

  useEffect(() => {
    if (!canSendInternalMessage) {
      setAlertRecipients({ groups: [], users: [] });
      setAlertTarget("");
      return;
    }
    api.get("/operational-alerts/recipients").then(({ data }) => {
      setAlertRecipients(data);
      setAlertTarget((current) => current || (user?.role === "segreteria" ? "role:fisioterapista" : "role:segreteria"));
    }).catch(() => {});
  }, [canSendInternalMessage, user?.role]);

  const createOperationalAlert = async () => {
    const text = alertText.trim();
    if (!text) {
      toast.error("Scrivi il testo del messaggio");
      return;
    }
    if (!alertTarget) {
      toast.error("Seleziona il destinatario");
      return;
    }
    const payload = { text };
    if (alertTarget.startsWith("user:")) payload.target_user_id = alertTarget.slice(5);
    else if (alertTarget.startsWith("role:")) payload.target_role = alertTarget.slice(5);

    setAlertSaving(true);
    try {
      await api.post("/operational-alerts", payload);
      setAlertText("");
      setAlertComposerOpen(false);
      toast.success("Messaggio interno inviato");
    } catch {
      toast.error("Impossibile inviare il messaggio");
    } finally {
      setAlertSaving(false);
    }
  };

  const closeSecretaryAlert = async () => {
    if (!activeSecretaryAlert) return;
    const currentId = activeSecretaryAlert.id;
    try {
      await api.post(`/operational-alerts/${currentId}/read`);
    } catch { /* noop */ }
    setSecretaryAlerts((rows) => rows.filter((row) => row.id !== currentId));
  };

  const openNote = async (n) => {
    try { await api.post(`/notes/${n.id}/read`); } catch { /* noop */ }
    setInboxOpen(false);
    navigate(`/pazienti/${n.patient_id}`);
    try { const { data } = await api.get("/notes/inbox"); setInbox(data); } catch { /* noop */ }
  };

  const acknowledgeUpdateNotice = async () => {
    try {
      await api.post("/auth/update-notice/read", { version: APP_VERSION });
      setUpdateNoticeOpen(false);
      toast.success("Aggiornamento segnato come letto");
    } catch {
      toast.error("Non è stato possibile salvare la conferma");
    }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const quickActions = [
    ...(can(user, "calendario", "create") ? [{ key: "appointment", label: "Appuntamento", icon: CalendarDays, color: "bg-blue-600" }] : []),
    ...(can(user, "pazienti", "create") ? [{ key: "patient", label: "Paziente", icon: Users, color: "bg-cyan-600" }] : []),
    ...(can(user, "palestra", "create") ? [{ key: "gym", label: "Ingresso palestra", icon: Dumbbell, color: "bg-emerald-600" }] : []),
    ...(can(user, "pagamenti", "create") ? [{ key: "payment", label: "Pagamento", icon: CreditCard, color: "bg-violet-600" }] : []),
    ...(can(user, "spese", "create") ? [{ key: "expense", label: "Spesa", icon: Receipt, color: "bg-amber-600" }] : []),
  ];

  return (
    <QuickCtx.Provider value={{ openModal }}>
      <div className="min-h-screen bg-background">
        {/* Sidebar desktop */}
        <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-slate-100 z-30" data-testid="sidebar">
          <div className="px-6 py-5">
            <img src="/logo.png" alt="LanuseiLAB" className="h-11 w-auto" data-testid="sidebar-logo" />
          </div>
          <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  }`}>
                <n.icon size={18} strokeWidth={1.8} />
                {navLabel(n)}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 leading-relaxed">Software realizzato da <span className="font-medium">Multiprojects.it</span><br />Maurizio Deplano — AI Powered Marketing Expert, Website Development Expert</p>
          </div>
        </aside>

        {/* Topbar */}
        <header className="fixed top-0 right-0 left-0 lg:left-64 h-16 bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-sm z-20 flex items-center gap-3 px-4 lg:px-8" data-testid="topbar">
          <button className="lg:hidden text-slate-600" data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)}><Menu size={22} /></button>
          <div className="lg:hidden flex items-center">
            <img src="/logo.png" alt="LanuseiLAB" className="h-8 w-auto" data-testid="topbar-logo-mobile" />
          </div>
          <div className="hidden sm:block flex-1"><GlobalSearch /></div>
          <div className="ml-auto flex items-center gap-3">
            {canSendInternalMessage && (
              <button
                data-testid="new-internal-message"
                onClick={() => setAlertComposerOpen(true)}
                className="p-2 rounded-xl text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors"
                title="Invia messaggio interno"
              >
                <Megaphone size={18} />
              </button>
            )}
            <div className="relative">
                <button data-testid="inbox-bell" onClick={() => setInboxOpen(!inboxOpen)} className="relative p-2 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors" title="Segnalazioni dal team">
                  <Bell size={18} />
                  {inbox?.unread > 0 && (
                    <span data-testid="inbox-badge" className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-4 h-4 px-0.5 flex items-center justify-center">{inbox.unread}</span>
                  )}
                </button>
                {inboxOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-100 rounded-2xl shadow-xl z-50 p-2 max-h-96 overflow-y-auto" data-testid="inbox-dropdown">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 px-2 py-1">Segnalazioni dal team</p>
                    {(inbox?.notes || []).length === 0 && <p className="text-sm text-slate-400 px-3 py-4" data-testid="inbox-empty">Nessuna segnalazione</p>}
                    {(inbox?.notes || []).slice(0, 10).map((n) => (
                      <button key={n.id} data-testid={`inbox-item-${n.id}`} onClick={() => openNote(n)}
                        className={`w-full text-left px-3 py-2 rounded-xl hover:bg-blue-50 ${!n.read ? "bg-amber-50/70" : ""}`}>
                        <p className="text-sm font-medium">{n.patient_name} <span className="text-xs font-normal text-slate-400">· {n.author_name}</span></p>
                        <p className="text-xs text-slate-500 truncate">{n.text}</p>
                        <p className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString("it-IT")}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            <span className="hidden md:block text-sm text-slate-500" data-testid="topbar-date">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-sm font-semibold" data-testid="user-avatar">
                {(user?.name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium leading-tight" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize" data-testid="user-role">{user?.role}</p>
              </div>
              <button data-testid="logout-btn" onClick={doLogout} className="text-slate-400 hover:text-red-500 transition-colors ml-1" title="Esci"><LogOut size={18} /></button>
            </div>
          </div>
        </header>

        {/* Contenuto */}
        <main className="pt-16 lg:pl-64 pb-24 lg:pb-8 min-h-screen">
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>

        {/* Bottom nav mobile */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white/85 backdrop-blur-xl border-t border-white/40 shadow-lg z-30" data-testid="bottom-nav">
          <div className="grid grid-cols-5 h-16 items-center">
            <NavLink to="/" data-testid="bottomnav-home" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-blue-600" : "text-slate-400"}`}>
              <LayoutDashboard size={20} />Home
            </NavLink>
            <NavLink to="/calendario" data-testid="bottomnav-calendar" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-blue-600" : "text-slate-400"}`}>
              <CalendarDays size={20} />Calendario
            </NavLink>
            <div className="flex justify-center">
              <button data-testid="bottomnav-plus" onClick={() => setQuickOpen(true)}
                className="w-14 h-14 -mt-6 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white flex items-center justify-center shadow-lg shadow-blue-500/30 active:scale-95 transition-transform">
                <Plus size={26} />
              </button>
            </div>
            <NavLink to="/pazienti" data-testid="bottomnav-patients" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-blue-600" : "text-slate-400"}`}>
              <Users size={20} />Pazienti
            </NavLink>
            <button data-testid="bottomnav-more" onClick={() => setMenuOpen(true)} className="flex flex-col items-center gap-0.5 text-[10px] text-slate-400">
              <Menu size={20} />Altro
            </button>
          </div>
        </nav>

        {/* Menu mobile full */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className="w-72" data-testid="mobile-menu">
            <SheetHeader><SheetTitle className="font-heading"><img src="/logo.png" alt="LanuseiLAB" className="h-9 w-auto" /></SheetTitle></SheetHeader>
            <div className="mt-4 space-y-1">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-600"}`}>
                  <n.icon size={18} />{navLabel(n)}
                </NavLink>
              ))}
            </div>
            <p className="absolute bottom-4 left-4 right-4 text-[10px] text-slate-400">Software realizzato da Multiprojects.it — Maurizio Deplano</p>
          </SheetContent>
        </Sheet>

        {/* Azioni rapide */}
        <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
          <DialogContent className="max-w-sm rounded-2xl" data-testid="quick-actions-modal">
            <DialogHeader><DialogTitle className="font-heading">Azioni rapide</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((a) => (
                <button key={a.key} data-testid={`quick-${a.key}`} onClick={() => openModal(a.key)}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all">
                  <span className={`w-11 h-11 rounded-2xl ${a.color} text-white flex items-center justify-center`}><a.icon size={20} /></span>
                  <span className="text-sm font-medium">{a.label}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Novità versione: mostrato una volta per utente dopo ogni aggiornamento */}
        <Dialog open={updateNoticeOpen} onOpenChange={setUpdateNoticeOpen}>
          <DialogContent className="max-w-2xl rounded-2xl border-2 border-amber-300 bg-amber-50" data-testid="update-notice-popup">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-amber-900">
                <History size={20} className="text-amber-600" />
                Nuovo aggiornamento v{APP_VERSION}
              </DialogTitle>
            </DialogHeader>
            {latestUpdate && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{latestUpdate.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{latestUpdate.date}</p>
                </div>
                <ul className="space-y-2 rounded-xl border border-amber-200 bg-white/70 p-4">
                  {latestUpdate.items.map((item) => (
                    <li key={item} className="flex gap-2 text-sm text-slate-700">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-amber-200 pt-3">
                  <button
                    type="button"
                    onClick={() => { setUpdateNoticeOpen(false); navigate("/aggiornamenti"); }}
                    className="px-4 py-2.5 rounded-xl border border-amber-300 bg-white text-amber-800 text-sm font-medium hover:bg-amber-100"
                  >
                    Vedi tutti gli aggiornamenti
                  </button>
                  <button
                    type="button"
                    onClick={acknowledgeUpdateNotice}
                    className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
                    data-testid="update-notice-read"
                  >
                    Ho letto
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Messaggistica interna rapida */}
        <Dialog open={alertComposerOpen} onOpenChange={setAlertComposerOpen}>
          <DialogContent className="max-w-md rounded-2xl" data-testid="internal-message-composer">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Megaphone size={18} className="text-amber-600" />
                Nuovo messaggio interno
              </DialogTitle>
            </DialogHeader>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Destinatario</label>
              <select
                value={alertTarget}
                onChange={(e) => setAlertTarget(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-200"
                data-testid="internal-message-target"
              >
                <option value="">Seleziona destinatario</option>
                <optgroup label="Gruppi">
                  {(alertRecipients.groups || []).map((g) => (
                    <option key={g.key} value={`role:${g.key}`}>{g.label}</option>
                  ))}
                </optgroup>
                {(alertRecipients.users || []).length > 0 && (
                  <optgroup label="Persone">
                    {alertRecipients.users.map((u) => (
                      <option key={u.id} value={`user:${u.id}`}>{u.name} · {u.role}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <textarea
              value={alertText}
              onChange={(e) => setAlertText(e.target.value)}
              rows={5}
              maxLength={1500}
              placeholder="Scrivi il messaggio..."
              className="w-full rounded-xl border border-amber-200 bg-amber-50/40 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-amber-200 resize-none"
              data-testid="internal-message-text"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAlertComposerOpen(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm">
                Annulla
              </button>
              <button type="button" disabled={alertSaving} onClick={createOperationalAlert}
                className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="internal-message-send">
                {alertSaving ? "Invio..." : "Invia messaggio"}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Messaggio interno: apertura automatica per il destinatario */}
        <Dialog open={Boolean(activeSecretaryAlert)} onOpenChange={(open) => !open && closeSecretaryAlert()}>
          <DialogContent className="max-w-lg rounded-2xl border-2 border-amber-300 bg-amber-50" data-testid="internal-message-popup">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-amber-900">
                <Megaphone size={20} className="text-amber-600" />
                Messaggio interno
              </DialogTitle>
            </DialogHeader>
            {activeSecretaryAlert && (
              <div className="space-y-4">
                <p className="text-base leading-relaxed text-slate-800 whitespace-pre-wrap">{activeSecretaryAlert.text}</p>
                <div className="flex items-center justify-between gap-3 border-t border-amber-200 pt-3">
                  <div className="text-xs text-slate-500">
                    <p>Da: <strong>{activeSecretaryAlert.author_name}</strong></p>
                    <p>{new Date(activeSecretaryAlert.created_at).toLocaleString("it-IT")}</p>
                  </div>
                  <button type="button" onClick={closeSecretaryAlert}
                    className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold"
                    data-testid="internal-message-read">
                    Ho letto
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modali globali */}
        {modal?.type === "appointment" && <AppointmentModal open prefill={modal.prefill} onClose={closeModal} onSaved={closeModal} />}
        {modal?.type === "patient" && <PatientModal open patient={modal.prefill?.patient} onClose={closeModal} onSaved={closeModal} />}
        {modal?.type === "payment" && <PaymentModal open prefill={modal.prefill} onClose={closeModal} />}
        {modal?.type === "expense" && <ExpenseModal open onClose={closeModal} />}
        {modal?.type === "gym" && <GymModal open onClose={closeModal} />}
      </div>
    </QuickCtx.Provider>
  );
}
